// Servicio de la caja de tienda: `/t/{slug}` + PIN.
//
// El token de caja NO viaja en `Authorization`, sino en `X-Caja-Token`. El
// navegador de una caja puede tener además una sesión de usuario normal (el
// dueño revisando sus reportes, alguien que probó la app en ese equipo), y dos
// credenciales peleando por la misma cabecera es un bug silencioso esperando.
import { apiClient } from './api';
import { API_BASE_URL } from '../config';

const BASE = `${API_BASE_URL}/api/store-access`;

// Una llave por tienda: un mismo equipo podría cubrir dos cajas distintas y no
// tiene por qué desloguear una al abrir la otra.
const llave = (slug) => `vondi_caja_sesion:${slug}`;

export const cajaSesion = {
  leer(slug) {
    try {
      const crudo = localStorage.getItem(llave(slug));
      if (!crudo) return null;
      const dato = JSON.parse(crudo);
      // Se compara contra la expiración guardada para no pedirle al servidor
      // que nos diga lo que ya sabemos. El servidor manda de todos modos.
      if (dato?.expires_at && new Date(dato.expires_at) < new Date()) {
        localStorage.removeItem(llave(slug));
        return null;
      }
      return dato?.token || null;
    } catch (e) {
      return null;
    }
  },

  guardar(slug, token, expiresAt) {
    localStorage.setItem(llave(slug), JSON.stringify({ token, expires_at: expiresAt }));
  },

  borrar(slug) {
    localStorage.removeItem(llave(slug));
  },
};

export const cajaService = {
  /** Datos de marca de la tienda, antes de teclear nada. */
  async tienda(slug) {
    return apiClient.request(`${BASE}/${encodeURIComponent(slug)}`, { method: 'GET' });
  },

  async entrar(slug, pin) {
    return apiClient.request(`${BASE}/${encodeURIComponent(slug)}/login`, {
      method: 'POST',
      body: { pin },
    });
  },

  /** ¿Sigue viva la sesión guardada en este dispositivo? */
  async sesion(slug) {
    return apiClient.request(`${BASE}/session/me`, {
      method: 'GET',
      headers: { 'X-Caja-Token': cajaSesion.leer(slug) || '' },
    });
  },

  async validarQr(slug, token) {
    return apiClient.request(`${BASE}/qr/validate`, {
      method: 'POST',
      headers: { 'X-Caja-Token': cajaSesion.leer(slug) || '' },
      body: { token },
    });
  },

  /*
    Ojo con lo que NO lleva esta llamada: `store_id`. La tienda sale de la
    sesión de caja en el servidor. Es el cambio que impide que un canje quede
    atribuido a la tienda equivocada, y por eso no debe volver aquí nunca.
  */
  /*
    Tampoco lleva `discount_amount` (Ricardo, 9-sep-2026). El ahorro lo calcula
    el servidor a partir del beneficio. Mientras fue un dato que mandaba esta
    pantalla, un fallo suyo se guardaba como si fuera un hecho: así salieron los
    canjes de «$0.20» sobre compras de mil quinientos pesos.
  */
  async canjear(slug, { token, benefitId, ticketNumber, scannerName, ticketTotal, fotoTicket }) {
    const cabeceras = { 'X-Caja-Token': cajaSesion.leer(slug) || '' };

    /*
      Con foto va como multipart; sin foto, como siempre.

      Se mantienen los dos caminos a propósito en vez de pasar todo a
      `FormData`: el canje sin foto es el que hoy funciona en tienda, y no hay
      razón para cambiarle el formato de envío por una función opcional. Menos
      superficie tocada, menos que pueda romperse en una caja un sábado.

      `apiClient` ya sabe no fijar `Content-Type` cuando el cuerpo es un
      FormData — tiene que ponerlo el navegador, porque solo él conoce el
      `boundary` del multipart.
    */
    if (fotoTicket) {
      const forma = new FormData();
      forma.append('token', token);
      forma.append('benefit_id', benefitId);
      forma.append('ticket_number', ticketNumber);
      forma.append('scanner_name', scannerName);
      forma.append('ticket_total', ticketTotal);
      forma.append('ticket_photo', fotoTicket);

      return apiClient.request(`${BASE}/qr/redeem`, {
        method: 'POST',
        headers: cabeceras,
        body: forma,
      });
    }

    return apiClient.request(`${BASE}/qr/redeem`, {
      method: 'POST',
      headers: cabeceras,
      body: {
        token,
        benefit_id: benefitId,
        ticket_number: ticketNumber,
        scanner_name: scannerName,
        ticket_total: ticketTotal,
      },
    });
  },
};
